import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useContentGridCore, ContentGridSummary } from "../cores/contentGrid";
import {
  ContentGridRow,
  consumeQueuedContentGridRows,
  createEmptyContentGridRow,
  isContentGridRowBlank,
  loadPersistedContentGridRows,
} from "../ui/utils/socialContentGridShare";

const SAVE_DEBOUNCE_MS = 800;

const normalizeCompanyId = (
  companyId?: string | number | null
): number | undefined => {
  if (companyId == null || companyId === "") return undefined;
  const parsed = Number(companyId);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const useSocialContentGridState = (companyId?: string | number | null) => {
  const intl = useIntl();
  const resolvedCompanyId = normalizeCompanyId(companyId);
  const {
    loadGrid,
    saveRows,
    addRowsBatch,
    listGrids,
    createGrid,
    migrateFromClient,
  } = useContentGridCore();

  const [gridId, setGridId] = useState<number | null>(null);
  const [gridName, setGridName] = useState("");
  const [gridStatus, setGridStatus] = useState("draft");
  const [grids, setGrids] = useState<ContentGridSummary[]>([]);
  const [rows, setRows] = useState<ContentGridRow[]>([createEmptyContentGridRow()]);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inboxMessage, setInboxMessage] = useState<string | null>(null);
  const saveTimerRef = useRef<number | null>(null);
  const skipSaveRef = useRef(false);
  const pendingConsumedRef = useRef(false);

  const applyGridPayload = useCallback((grid: { id: number; name: string; status: string; rows: ContentGridRow[] }) => {
    setGridId(grid.id);
    setGridName(grid.name);
    setGridStatus(grid.status);
    setRows(grid.rows?.length ? grid.rows : [createEmptyContentGridRow()]);
  }, []);

  const refreshGridList = useCallback(async () => {
    if (!resolvedCompanyId) return;
    const list = await listGrids(resolvedCompanyId);
    setGrids(list);
  }, [resolvedCompanyId, listGrids]);

  useEffect(() => {
    if (!resolvedCompanyId) return;

    const bootstrap = async () => {
      const pending = pendingConsumedRef.current
        ? []
        : consumeQueuedContentGridRows();
      pendingConsumedRef.current = true;

      const localRows = loadPersistedContentGridRows(companyId) ?? [];
      const hasLocal = localRows.some((row) => !isContentGridRowBlank(row));

      try {
        let grid = await loadGrid(resolvedCompanyId);
        if (hasLocal && (!grid.rows?.length || grid.rows.every(isContentGridRowBlank))) {
          grid = await migrateFromClient(
            resolvedCompanyId,
            localRows.filter((row) => !isContentGridRowBlank(row)),
            "Grilla importada"
          );
        }
        if (pending.length) {
          grid = await addRowsBatch(grid.id, resolvedCompanyId, pending);
          setInboxMessage(
            intl.formatMessage(
              {
                id: "app.socialContentGrid.receivedFromComposer",
                defaultMessage: "Se agregaron {count} publicaciones desde el posteador.",
              },
              { count: pending.length }
            )
          );
        }
        skipSaveRef.current = true;
        applyGridPayload(grid);
        await refreshGridList();
      } catch {
        if (pending.length) {
          setRows([...pending, createEmptyContentGridRow()]);
        } else if (hasLocal) {
          setRows(localRows);
        }
      } finally {
        setHydrated(true);
      }
    };

    bootstrap();
  }, [companyId, resolvedCompanyId, intl, loadGrid, addRowsBatch, migrateFromClient, applyGridPayload, refreshGridList]);

  useEffect(() => {
    if (!hydrated || !resolvedCompanyId || !gridId || skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }

    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = window.setTimeout(async () => {
      setSaving(true);
      try {
        const saved = await saveRows(gridId, resolvedCompanyId, rows);
        skipSaveRef.current = true;
        applyGridPayload(saved);
      } finally {
        setSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [rows, hydrated, resolvedCompanyId, gridId, saveRows, applyGridPayload]);

  const switchGrid = useCallback(
    async (nextGridId: number) => {
      if (!resolvedCompanyId) return;
      const grid = await loadGrid(resolvedCompanyId, nextGridId);
      skipSaveRef.current = true;
      applyGridPayload(grid);
    },
    [resolvedCompanyId, loadGrid, applyGridPayload]
  );

  const createNewGrid = useCallback(async () => {
    if (!resolvedCompanyId) return;
    const grid = await createGrid(resolvedCompanyId, {
      name: `Grilla ${new Date().toLocaleDateString()}`,
    });
    skipSaveRef.current = true;
    applyGridPayload(grid);
    await refreshGridList();
  }, [resolvedCompanyId, createGrid, applyGridPayload, refreshGridList]);

  const duplicateCurrentGrid = useCallback(async () => {
    if (!resolvedCompanyId || !gridId) return;
    const grid = await createGrid(resolvedCompanyId, {
      duplicateFromId: gridId,
    });
    skipSaveRef.current = true;
    applyGridPayload(grid);
    await refreshGridList();
  }, [resolvedCompanyId, gridId, createGrid, applyGridPayload, refreshGridList]);

  const syncFromServer = useCallback(
    (grid: { id: number; name: string; status: string; rows: ContentGridRow[] }) => {
      skipSaveRef.current = true;
      applyGridPayload(grid);
    },
    [applyGridPayload]
  );

  const refreshCurrentGrid = useCallback(async () => {
    if (!resolvedCompanyId || !gridId) return;
    const grid = await loadGrid(resolvedCompanyId, gridId);
    syncFromServer(grid);
  }, [resolvedCompanyId, gridId, loadGrid, syncFromServer]);

  return {
    gridId,
    gridName,
    gridStatus,
    grids,
    rows,
    setRows,
    hydrated,
    saving,
    inboxMessage,
    setInboxMessage,
    switchGrid,
    createNewGrid,
    duplicateCurrentGrid,
    refreshGridList,
    syncFromServer,
    refreshCurrentGrid,
  };
};
